import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import VehicleDetails from "./VehicleDetails";
import NotFoundPage from "./NotFoundPage";
import { getApiBaseUrl } from "./config/api";

const API_URL = getApiBaseUrl();

function VehicleDetailsPage({ reference, navigateTo }) {
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchVehicle() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_URL}/api/vehicles/${encodeURIComponent(reference)}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Erreur API: ${response.status}`);
        }

        const vehicleData = await response.json();

        if (isMounted) {
          setVehicle(vehicleData);
          setLoading(false);
        }
      } catch (fetchError) {
        if (isMounted) {
          setError(fetchError.message);
          setLoading(false);
        }
      }
    }

    fetchVehicle();

    return () => {
      isMounted = false;
    };
  }, [reference]);

  const displayTitle = useMemo(() => {
    if (!vehicle) {
      return "Fiche véhicule";
    }

    return [vehicle.marque, vehicle.modele].filter(Boolean).join(" ") || "Véhicule";
  }, [vehicle]);

  const description = useMemo(() => {
    if (!vehicle) {
      return "Consultez la fiche détaillée du véhicule sélectionné chez FLIP MY CAR.";
    }

    const details = [
      vehicle.spiderVersion || vehicle.version || vehicle.modelePrecis,
      Number.isFinite(Number(vehicle.kilometrage)) ? `${Number(vehicle.kilometrage).toLocaleString("fr-FR")} km` : null,
      Number.isFinite(Number(vehicle.prix)) ? `${Number(vehicle.prix).toLocaleString("fr-FR")} €` : null,
    ].filter(Boolean);

    return `${displayTitle}${details.length ? ` - ${details.join(" • ")}` : ""} | FLIP MY CAR`;
  }, [vehicle, displayTitle]);

  const canonicalUrl = `https://flipmycar.fr/vehicules/${encodeURIComponent(reference)}`;

  if (loading) {
    return (
      <div style={{ padding: "50px", textAlign: "center" }}>
        <Helmet>
          <title>CHARGEMENT VÉHICULE | FLIP MY CAR</title>
          <meta name="description" content="Chargement de la fiche véhicule." />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <h2>⏳ Chargement du véhicule...</h2>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <>
        <Helmet>
          <title>VEHICULE INTROUVABLE | FLIP MY CAR</title>
          <meta name="description" content="Ce véhicule est introuvable ou indisponible." />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <NotFoundPage
          navigateTo={navigateTo}
          title="Vehicule introuvable"
          message={error || "Le vehicule demande est indisponible."}
          ctaLabel="Retour au stock"
          ctaPath="/nos-vehicules"
        />
      </>
    );
  }

  const ogImage = vehicle.images && vehicle.images.length > 0
    ? vehicle.images[0]
    : "https://flipmycar.fr/logo2.png";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Car",
    name: displayTitle,
    description,
    image: vehicle.images && vehicle.images.length > 0 ? vehicle.images.slice(0, 5) : undefined,
    brand: vehicle.marque ? { "@type": "Brand", name: vehicle.marque } : undefined,
    model: vehicle.modele || undefined,
    vehicleTransmission: vehicle.boiteVitesse || undefined,
    fuelType: vehicle.carburant || undefined,
    mileageFromOdometer: Number.isFinite(Number(vehicle.kilometrage))
      ? { "@type": "QuantitativeValue", value: Number(vehicle.kilometrage), unitCode: "KMT" }
      : undefined,
    offers: Number.isFinite(Number(vehicle.prix))
      ? { "@type": "Offer", price: Number(vehicle.prix), priceCurrency: "EUR", availability: "https://schema.org/InStock" }
      : undefined,
    url: canonicalUrl,
  };

  return (
    <>
      <Helmet>
        <title>{`${displayTitle} | FLIP MY CAR`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${displayTitle} | FLIP MY CAR`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${displayTitle} | FLIP MY CAR`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <VehicleDetails vehicle={vehicle} onClose={() => window.history.back()} />
    </>
  );
}

export default VehicleDetailsPage;